import { useEffect, useRef, useState } from "react";
import { Disclosure } from "@headlessui/react";
import {
  BsArrowRight,
  BsBoxSeam,
  BsCalendar3,
  BsCheck2,
  BsCheckCircleFill,
  BsChevronDown,
  BsClipboard,
  BsCreditCard,
  BsFileEarmarkPdf,
  BsGeoAlt,
  BsLightningCharge,
  BsShop,
  BsTruck,
  BsXCircleFill,
} from "react-icons/bs";

const STATUS_BADGES = {
  pending: { label: "Awaiting Pickup", className: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500" },
  collected: { label: "Collected", className: "bg-sky-50 text-sky-800 ring-sky-200", dot: "bg-sky-500" },
  enroute: { label: "En Route", className: "bg-purple-50 text-purple-800 ring-purple-200", dot: "bg-purple-600" },
  ready_for_collection: { label: "Ready for Collection", className: "bg-teal-50 text-teal-800 ring-teal-200", dot: "bg-teal-500" },
  delivered: { label: "Delivered", className: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500" },
  cancelled: { label: "Cancelled", className: "bg-red-50 text-red-700 ring-red-200", dot: "bg-red-500" },
  awaiting_payment: { label: "Awaiting Payment", className: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500" },
};

const PAYMENT_BADGES = {
  paid: { label: "Paid", className: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  created: { label: "Awaiting payment", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  cancelled: { label: "Cancelled", className: "bg-red-50 text-red-700 ring-red-200" },
};

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })
    : "—";
};

function Detail({ icon, label, children }) {
  return (
    <div className="flex gap-3 min-w-0">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-purple-600 shadow-sm ring-1 ring-gray-100" aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <div className="text-sm text-gray-900 break-words">{children}</div>
      </div>
    </div>
  );
}

function ProgressTrack({ steps, currentIndex }) {
  const progress = steps.length > 1 ? (currentIndex / (steps.length - 1)) * 100 : 0;
  return (
    <div className="relative mt-5" role="img" aria-label={`Progress: ${steps[currentIndex].label}`}>
      <div className="absolute left-4 right-4 top-4 h-1 rounded-full bg-gray-100" aria-hidden="true">
        <div className="h-full rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-500 transition-all duration-700" style={{ width: `${progress}%` }} />
      </div>
      <ol className="relative flex justify-between" aria-hidden="true">
        {steps.map((step, index) => {
          const done = index < currentIndex;
          const current = index === currentIndex;
          return (
            <li key={step.label} className="flex flex-col items-center w-16">
              <span className="relative flex h-9 w-9 items-center justify-center">
                {current && <span className="absolute inset-0 rounded-full bg-purple-400/40 motion-safe:animate-ping" />}
                <span
                  className={`relative flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors ${
                    done ? "bg-purple-600 text-white" : current ? "bg-white text-purple-700 ring-2 ring-purple-600" : "bg-white text-gray-300 ring-2 ring-gray-100"
                  }`}
                >
                  {done ? <BsCheck2 /> : step.icon}
                </span>
              </span>
              <span className={`mt-1.5 text-[11px] sm:text-xs font-medium text-center leading-tight ${done || current ? "text-purple-800" : "text-gray-400"}`}>
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function OrderCard({ order, defaultOpen = false }) {
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const deliveryStatus = order.delivery_status ?? "pending";
  const isCancelled = order.status === "cancelled" || deliveryStatus === "cancelled";
  // Only uncollected orders are blocked on payment. Orders already on their way
  // or delivered can be unpaid too (e.g. monthly invoicing), so no prompt there.
  const needsPayment = order.status === "created" && !isCancelled && deliveryStatus === "pending";
  const badgeKey = isCancelled ? "cancelled" : needsPayment ? "awaiting_payment" : deliveryStatus;
  const badge = STATUS_BADGES[badgeKey] ?? STATUS_BADGES.pending;
  const paymentBadge = PAYMENT_BADGES[order.status] ?? { label: order.status ?? "Unknown", className: "bg-gray-50 text-gray-700 ring-gray-200" };

  const steps = [
    { key: "pending", label: "Booked", icon: <BsBoxSeam /> },
    { key: "collected", label: "Collected", icon: <BsCheckCircleFill /> },
    { key: "enroute", label: "En route", icon: <BsTruck /> },
    order.destination_province?.name === "Limpopo" && { key: "ready_for_collection", label: "Ready", icon: <BsShop /> },
    { key: "delivered", label: "Delivered", icon: <BsCheckCircleFill /> },
  ].filter(Boolean);
  const currentIndex = Math.max(0, steps.findIndex((step) => step.key === deliveryStatus));

  const from = order.departure_town?.name || order.departure_province?.name || "—";
  const to = order.destination_town?.name || order.destination_province?.name || "—";
  const collectionDate = formatDate(order.collection_date);

  // payment_url is built by the API and already accounts for wallet credit.
  // The fallback covers older orders created before payment_url was stored.
  const fallbackAmount = (order?.price?.value ?? 0) * (order?.quantity ?? 1);
  const payLink =
    order?.payment_url ||
    `https://www.payfast.co.za/eng/process?cmd=_paynow&receiver=11799876&item_name=Parcelin%20Order+${order?.reference_number}&item_description=Parcelin%20Order+R${fallbackAmount}&amount=${fallbackAmount}&return_url=https://parcelin.co.za/payment-success&cancel_url=https://parcelin.co.za/parcels`;

  const copyReference = (event) => {
    event.stopPropagation();
    navigator.clipboard?.writeText(order.reference_number ?? "").catch(() => {});
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <article className={`bg-white rounded-2xl border shadow-sm transition-shadow hover:shadow-md ${open ? "border-purple-200" : "border-gray-100"}`}>
          <div className="px-5 pt-5 pb-4">
            <div className="flex flex-col-reverse items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 flex-wrap text-lg text-gray-900">
                  <span>{from}</span>
                  <BsArrowRight className="text-purple-400 flex-shrink-0" aria-label="to" />
                  <span>{to}</span>
                </h3>
                <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-sm text-gray-500">
                  <button
                    type="button"
                    onClick={copyReference}
                    className="inline-flex items-center gap-1.5 font-mono font-semibold text-gray-700 hover:text-purple-700"
                    aria-label={`Copy tracking number ${order.reference_number}`}
                  >
                    #{order.reference_number}
                    {copied ? <BsCheck2 className="text-emerald-600" aria-hidden="true" /> : <BsClipboard className="text-gray-400" aria-hidden="true" />}
                  </button>
                  <span className="inline-flex items-center gap-1.5">
                    <BsCalendar3 className="text-gray-400" aria-hidden="true" /> {collectionDate}
                  </span>
                </div>
              </div>
              <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ring-1 ${badge.className}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} aria-hidden="true" />
                {badge.label}
              </span>
            </div>

            {isCancelled ? (
              <p className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <BsXCircleFill aria-hidden="true" /> This order was cancelled.
              </p>
            ) : (
              <ProgressTrack steps={steps} currentIndex={currentIndex} />
            )}
          </div>

          {needsPayment && (
            <div className="mx-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
              <p className="flex-1 flex items-center gap-2 text-sm text-amber-900">
                <BsCreditCard className="flex-shrink-0" aria-hidden="true" />
                Payment is needed before this parcel can be collected.
              </p>
              <a href={payLink} className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 px-5 py-2 text-sm font-medium text-white transition-colors">
                Pay Now <BsArrowRight aria-hidden="true" />
              </a>
            </div>
          )}

          <Disclosure.Button className="flex w-full items-center justify-center gap-2 border-t border-gray-100 py-2.5 text-sm font-medium text-purple-700 hover:bg-purple-50/60 transition-colors rounded-b-2xl">
            {open ? "Hide details" : "View details"}
            <BsChevronDown className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </Disclosure.Button>

          <Disclosure.Panel static className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
            <div className="overflow-hidden">
              <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-5 rounded-b-2xl grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Detail icon={<BsGeoAlt />} label="Pickup address">{order.pickup_location || "—"}</Detail>
                <Detail icon={<BsGeoAlt />} label="Delivery address">{order.drop_off_location || to}</Detail>
                <Detail icon={<BsLightningCharge />} label="Delivery speed">{order.price?.price_type?.name || "Standard"}</Detail>
                <Detail icon={<BsCreditCard />} label="Payment">
                  <span className={`inline-flex mt-0.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${paymentBadge.className}`}>{paymentBadge.label}</span>
                </Detail>
                {order.id && (
                  <div className="sm:col-span-2">
                    <a
                      href={`${process.env.REACT_APP_API_URL}parcel_orders/${order.id}/waybill`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-purple-700 ring-1 ring-purple-200 hover:bg-purple-50 transition-colors"
                    >
                      <BsFileEarmarkPdf aria-hidden="true" /> Download waybill
                    </a>
                  </div>
                )}
              </div>
            </div>
          </Disclosure.Panel>
        </article>
      )}
    </Disclosure>
  );
}
